import { useMemo, useState } from "react";

import MagnifyIcon from "mdi-react/MagnifyIcon";
import { Modal, Table, Form, Button, InputGroup } from "react-bootstrap";
import { services, durationOptions, appSettings } from "../../config";
import useDebounce, {
  useEffectOnce,
  useIsAdmin,
  useUnits,
  useUpdateEffect,
} from "../../utils/hooks";
import { queryActions } from "../../utils/reactQueryActions";
import "./../../assets/scss/item-select-modal.scss";
import currency from "currency.js";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import NumberCustomInput from "../utils/NumberCustomInput";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import Select from "react-select";
import { useEffect } from "react";
import {
  calculateMarkUp_Incentive_Margin,
  pcsToTons,
  qtyFormat,
  qtyFormatToString,
  tonsToPcs,
  unitsResolver,
} from "../../utils/helpers";
import ConfirmDialog from "../ConfirmDialogue";
import { useAuth } from "../../hooks/useAuth";
import { lowerCase } from "lodash";
import { useStoreActions, useStoreState } from "easy-peasy";
import { toast } from "react-toastify";
import { truncate } from "lodash";
import SelectBatchDialog from "./SelectBatchDialog";
import { isEmpty } from "lodash";
import { QuantityLabel } from "../utils/ConvertQuantity";

export default function EditPosItemModal({
  selectedItemToEdit,
  setEditedItemIndex,
  handleEditItem,
  saleTypes = [],
  currencySymbol = "",
  itemsApiUrl = "/api/items",
  usage = "sale", // selector -
  mode,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const { backendUrl } = useAuth();
  const itemMeasurements = useStoreState(state => state.itemMeasurements);
  const generalSettings = useStoreState(state => state.generalSettings);
  const isAdmin = useIsAdmin();
  const [unitPriceInTons, setUnitPriceInTons] = useState(0);
  const [unitPriceInPcs, setUnitPriceInPcs] = useState(0);
  const [selectedItem, setselectedItem] = useState(selectedItemToEdit);
  const [firstKey, setFirstKey] = useState(true);

  const {
    getUnitsByUomById,
    isBaseUOM,
    convertMainUOM_To_BaseUOM,
    getUOMByUOMId,
    convertBaseUOM_To_MainUOM,
  } = useUnits();

  const formik = useFormik({
    initialValues: {
      Quantity: 1,
      UnitPrice: "",
      PriceSold: "",
      durationNumber: 0,
      durationValue: "Month",
      Warranty: false,
      Discount: 0,
      ironRodQuantity: 0,
      saleType: "",
      requirePermission: false,
      Size: "",
      remark: "",
      serialNumberItems: [],
      currency: generalSettings?.prevailingCurrency,
      conversionAmount: generalSettings.dollarInBaseCurrency,

      // UOM
      uomId: "",
      baseUOMUnitPrice: "",
    },
    validationSchema: yup.object().shape({
      ironRodQuantity: yup
        .number()
        .required("required")
        .moreThan(0, "must be greater than 0"),
      UnitPrice: yup.number().required(),
      PriceSold: yup.number().required(),
    }),
    onSubmit: async values => {
      const quantityInStock = selectedItem?.quantityInStock;

      // USER ENTERED QTY - NOT TOUCHED (CONVERTED ETC)
      const userEnteredQty = values.ironRodQuantity;

      if (lowerCase(selectedItem.Item_Type) !== "service") {
        if (
          Number(values.Quantity) > quantityInStock &&
          // IF IT IS NOT A RESTAURANT OR BAR ITEM (THOSE ITEMS ARE PROCESSED DIFFERENTLY ON THE SERVER)
          !(selectedItem?.restaurantMenu || selectedItem?.barMenu)
        ) {
          return formik.setFieldError(
            "ironRodQuantity",
            `Not enough item in stock`
          );
        }

        // check if they have enough sizes in stock
        if (values.Size && appSettings.hasSize) {
          const selectedSize = selectedItem.sizes.find(
            el => el.Size_Color === values.Size
          );
          if (Number(values.Quantity) > selectedSize.Quantity) {
            return formik.setFieldError(
              "Size",
              `Not enough item in stock for the selected Size - ${values.Size}`
            );
          }
        }
      }

      // GIGC: TONS AND PCS
      const theUnitPrice =
        lowerCase(values.saleType) === "tons" ||
        lowerCase(values.saleType) !== "pieces"
          ? unitPriceInTons
          : unitPriceInPcs;

      if (
        Number(theUnitPrice) > Number(values.PriceSold) &&
        !appSettings.ignoreSalePermissions &&
        !selectedItem?.uomId
      ) {
        if (
          !(await ConfirmDialog({
            description:
              "Selling Price entered will require permission \n Do you wish to continue",
          }))
        ) {
          return;
        } else {
          values.requirePermission = true;
        }
      }

      values.Warrant_Duration = values?.Warrant_Duration || "";

      values.PriceSold = currency(values.PriceSold).value;

      values.defaultSubTotal = currency(values.UnitPrice).multiply(
        selectedItem?.uomId
          ? userEnteredQty
          : lowerCase(values.saleType) === "tons"
          ? pcsToTons(values.Quantity, selectedItem.Item_Desc, itemMeasurements)
          : values.Quantity
      ).value;

      values.SubTotal = currency(values.PriceSold).multiply(
        selectedItem?.uomId
          ? userEnteredQty
          : lowerCase(values.saleType) === "tons"
          ? pcsToTons(values.Quantity, selectedItem.Item_Desc, itemMeasurements)
          : values.Quantity
      ).value;

      values.Discount = currency(values.Discount).multiply(
        selectedItem?.uomId
          ? userEnteredQty
          : lowerCase(values.saleType) === "tons"
          ? pcsToTons(values.Quantity, selectedItem.Item_Desc, itemMeasurements)
          : values.Quantity
      ).value;

      // Get Profit Based on batch we are picking from---------------------------------------------------
      const quantityToSell = values.Quantity;
      let remainder = quantityToSell;
      const updatedBatchDataToSave = [];
      let measurement = itemMeasurements.find(
        measurement => measurement.Size === selectedItem.Item_Desc
      );

      if (
        lowerCase(values.saleType) !== "tons" &&
        lowerCase(values.saleType) !== "pieces"
      ) {
        measurement = {
          Quantity: 1,
        };
      }

      if (lowerCase(values.saleType) === "tons" && !measurement) {
        return toast.error("This item is not sold in Tons");
      }

      const priceSoldPerUnit =
        lowerCase(values.saleType) === "tons"
          ? currency(values.PriceSold).divide(measurement?.Quantity || 1).value
          : values.PriceSold;

      //Set if can selected Batch
      let selectedBatches = [];
      if (generalSettings.selectBatchDuringInvoicing && usage === "sale") {
        // BLOCK - NO LOGIC
        if (selectedItem?.uomId) {
          return toast.info(
            "Selecting Batch is not allowed for this deployment (UOM)"
          );
        }

        const { batches } = await SelectBatchDialog({
          batches: selectedItem?.batches,
          quantityToSell: values.Quantity,
          generalSettings,
          previouslySelectedBatches: selectedItem?.selectedBatches || [],
        });

        selectedBatches = batches;
      }

      for (let [index, batch] of (!isEmpty(selectedBatches)
        ? selectedBatches
        : !isEmpty(selectedItem?.batches)
        ? selectedItem.batches
        : []
      ).entries()) {
        let pcsUnitCost = currency(batch.UnitCost, {
          precision: 6,
        }).divide(measurement?.Quantity || 1).value;

        // UOM LOGIC =
        if (selectedItem?.uomId && typeof getUOMByUOMId === "function") {
          const uom = getUOMByUOMId({ uomId: selectedItem.uomId });
          pcsUnitCost = currency(batch.UnitCost, { precision: 6 }).divide(
            uom?.baseQty || 1
          ).value;
        }

        const {
          calculatedMarkUp,
          calculatedIncentive,
        } = calculateMarkUp_Incentive_Margin({
          ...selectedItem,
          UnitCost: pcsUnitCost,
          PriceSold: priceSoldPerUnit,
        });

        if (Number(batch.Quantity) >= Number(remainder)) {
          // means we are at the last
          updatedBatchDataToSave.push({
            unitCost: pcsUnitCost,
            quantity: remainder,
            totalUnitCost: currency(pcsUnitCost, {
              symbol: "",
              separator: "",
            })
              .multiply(remainder)
              .format(),
            totalUnitPrice: currency(priceSoldPerUnit, {
              symbol: "",
              separator: "",
            })
              .multiply(remainder)
              .format(),
            totalMargin: currency(selectedItem?.Margin || 0)
              .divide(measurement?.Quantity || 1)
              .multiply(remainder).value,
            totalIncentive: currency(calculatedIncentive || 0)
              .divide(measurement?.Quantity || 1)
              .multiply(remainder).value,
            totalMarkUp: currency(calculatedMarkUp || 0).multiply(remainder)
              .value,
          });

          if (lowerCase(values.saleType) === "pieces") {
            values.UnitCost = pcsUnitCost;
          } else {
            values.UnitCost = batch.UnitCost;
          }

          if (
            isBaseUOM({
              uomId: selectedItem?.uomId,
              uomName: values.saleType,
            })
          ) {
            values.UnitCost = pcsUnitCost;
          } else {
            values.UnitCost = batch.UnitCost;
          }

          break;
        } else {
          updatedBatchDataToSave.push({
            unitCost: pcsUnitCost,
            quantity: batch.Quantity,
            totalUnitCost: currency(pcsUnitCost, {
              symbol: "",
              separator: "",
            })
              .multiply(batch.Quantity)
              .format(),
            totalUnitPrice: currency(priceSoldPerUnit, {
              symbol: "",
              separator: "",
            })
              .multiply(batch.Quantity)
              .format(),
            totalMargin: currency(selectedItem?.Margin || 0).multiply(
              batch.Quantity
            ).value,
            totalIncentive: currency(calculatedIncentive || 0).multiply(
              batch.Quantity
            ).value,
            totalMarkUp: currency(calculatedMarkUp || 0).multiply(
              batch.Quantity
            ).value,
          });

          remainder = Number(
            currency(remainder, {
              symbol: "",
              separator: "",
            })
              .subtract(batch.Quantity)
              .format()
          );
        }
      }

      const totalProfit = updatedBatchDataToSave
        .map(
          el =>
            currency(el.totalUnitPrice)
              .subtract(el.totalUnitCost)
              .subtract(el.totalIncentive).value
        )
        .reduce(
          (a, b) =>
            currency(a, {
              precision: 2,
            }).add(b).value,
          0
        );

      values.Profit = totalProfit;

      values.totalUnitCost = updatedBatchDataToSave
        .map(el => el.totalUnitCost)
        .reduce((a, b) => currency(a).add(b).value, 0);

      // PROFIT WHEN MAIN UOM IS SOLD
      // NO INCENTIVE YET - UOM MODULE WHEN MAIN IS SOLD
      if (
        selectedItem?.uomId &&
        !isBaseUOM({
          uomId: selectedItem?.uomId,
          uomName: values.saleType,
        })
      ) {
        values.Profit = currency(values.PriceSold)
          .multiply(userEnteredQty)
          .subtract(values.totalUnitCost).value;
      }
      // ================================================

      values.Total_Incentive = updatedBatchDataToSave
        .map(el => el.totalIncentive)
        .reduce(
          (a, b) =>
            currency(a, {
              precision: 2,
            }).add(b).value,
          0
        );

      //  For Service Overwrite ----------------------------------------------------------
      if (lowerCase(selectedItem.Item_Type) === "service") {
        values.Profit = currency(priceSoldPerUnit)
          .subtract(selectedItem.UnitCost)
          .multiply(values.Quantity).value;
        values.Total_Incentive = currency(selectedItem?.Incentive).multiply(
          values.Quantity
        ).value;
      }

      const saleData = {
        ...selectedItem,
        Serial_Number: selectedItem.Item_Desc,
        ...values,
        selectedBatches,

        // NEW UOM LOGIC
        // =============
      };

      handleEditItem(saleData);
    },
  });

  const getBatches = async selectedItemFromPopover => {
    /*
      if we are editing an invoice  we need to get batches that was sold before
    */
    return new Promise(async (resolve, reject) => {
      try {
        setIsLoading(true);
        let response = await fetch(
          `${backendUrl}${itemsApiUrl}/batches-for-sales/${
            selectedItemFromPopover.Bar_Code
          }`,
          {
            method: "GET",
            headers: {
              Accept: "Application/json",
              "Content-Type": "Application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          response = await response.json();
          throw new Error();
        } else {
          let {
            data: { batches, sizes },
          } = await response.json();

          // --------------------------------------------------------------------
          if (mode === "edit" && !isEmpty(selectedItem.soldBatches)) {
            batches = [
              ...batches.map(el => {
                // check if batch was sold before
                const foundSoldBatches = selectedItem.soldBatches.filter(
                  soldbatch => soldbatch.Bar_Code === el.Bar_Code
                );

                if (!isEmpty(foundSoldBatches)) {
                  const totalSoldQty = foundSoldBatches
                    .map(el => el.Quantity)
                    .reduce((a, b) => currency(a).add(b).value, 0);
                  el.Quantity = currency(totalSoldQty).add(el.Quantity).value;
                }

                return el;
              }),
            ];
          }
          //---------------------------------------------------------

          selectedItem.batches = batches;
          resolve();
          // handleSelectedItem({ ...selectedItemFromPopover, batches, sizes });
        }
      } catch (err) {
        console.log(err);
        toast.error("Unable to load batches, Try again");
        reject();
      } finally {
        setIsLoading(false);
      }
    });
  };

  useEffectOnce(() => {
    console.log("once time -", selectedItem);
    if (
      isEmpty(selectedItem.batches) &&
      lowerCase(selectedItem.Item_Type) !== "service"
    ) {
      getBatches(selectedItem);
    }

    let UnitPrice = currency(selectedItem.UnitPrice).value;
    const PriceSold = currency(selectedItem.PriceSold).value;

    // DATA IS ALWAYS SAVED IN BASE UOM
    // SINCE WE WANT TO EDIT WE NEED TO CONVERT TO THE USER ENTERED UOM
    let userEnteredQty = selectedItem.Quantity;
    if (selectedItem?.uomId) {
      const uom = getUOMByUOMId({ uomId: selectedItem?.uomId });
      if (selectedItem.saleType === uom.uom) {
        const { mainUomQty } = convertBaseUOM_To_MainUOM({
          quantity: selectedItem.Quantity,
          uomId: selectedItem?.uomId,
        });
        userEnteredQty = mainUomQty;
      } else {
        UnitPrice = selectedItem.baseUOMUnitPrice;
      }
    }

    // BASE IS HERE
    const Quantity = currency(selectedItem.Quantity, {
      precision: 2,
    }).value;

    const ironRodQuantity = selectedItem?.uomId
      ? userEnteredQty
      : lowerCase(selectedItem.saleType) === "tons"
      ? pcsToTons(Quantity, selectedItem.Item_Desc, itemMeasurements)
      : Quantity;

    const Discount = currency(selectedItem.Discount).divide(ironRodQuantity)
      .value;

    // For iron rod
    formik.setValues({
      ...formik.values,
      Quantity,
      UnitPrice,
      PriceSold: PriceSold,
      ...(Number(Discount) ? { Discount } : { Discount: "" }),
      ironRodQuantity,
      saleType: selectedItem.saleType,
      requirePermission: selectedItem.requirePermission,
      Size: selectedItem?.Serial_Number,
      remark: selectedItem?.remark,
      serialNumberItems: selectedItem.serialNumberItems,

      // UOM LOGIC
      uomId: selectedItem?.uomId || "",
      baseUOMUnitPrice: selectedItem?.baseUOMUnitPrice || "",
    });

    // NOT SURE THIS IS USED
    if (selectedItem.Warranty) {
      const duration = selectedItem.Warrant_Duration.split(" ");
      formik.setFieldValue("Warranty", true);
      formik.setFieldValue("durationNumber", duration[0]);
      formik.setFieldValue("durationValue", duration[1]);
    }

    // GIGC: LOGIC
    setUnitPriceInTons(selectedItem.UnitPrice);
    setUnitPriceInPcs(
      selectedItem.Tax ? selectedItem.Tax : selectedItem.UnitPrice
    );

    // FOCUS
    setTimeout(() => {
      const el = document.querySelector('input[name="ironRodQuantity"]');
      if (el) {
        el.focus();
        el.select();
        el.scrollIntoView({ behavior: "smooth" });
      }
    }, 50);
  });

  const handleDiscountChange = discount => {
    const finalSellingPrice = currency(formik.values.UnitPrice, {
      symbol: "",
      separator: "",
    })
      .subtract(discount)
      .format();
    formik.setFieldValue("PriceSold", finalSellingPrice);
    formik.setFieldValue("Discount", discount);
  };

  const handleSaleType = value => {
    if (selectedItem?.uomId) {
      // UOM ============================================
      // WE ARE EDITING WE NEED TO PULL FROM DIFFERENT SOURCE
      const price = isBaseUOM({
        uomId: selectedItem?.uomId,
        uomName: value,
      })
        ? selectedItem?.originalBaseUOMUnitPrice || 0 // IT IS SAVED HERE AND NAMED CORRECTLY
        : selectedItem.originalUnitPrice || 0; // IT IS SAVED HERE AND NAMED CORRECTLY

      formik.setFieldValue("UnitPrice", price);
      formik.setFieldValue("PriceSold", price);
    } else if (lowerCase(value) === "tons") {
      formik.setFieldValue("UnitPrice", unitPriceInTons);
      formik.setFieldValue("PriceSold", unitPriceInTons);
    } else {
      formik.setFieldValue("UnitPrice", unitPriceInPcs);
      formik.setFieldValue("PriceSold", unitPriceInPcs);
    }
  };

  // SET QUANTITY
  // IT MUST BE IN BASE QTY
  useEffect(() => {
    let quantity = 0;

    // NEW UOM CODE
    // ============
    if (selectedItem?.uomId) {
      const uomId = selectedItem.uomId;
      quantity = !isBaseUOM({
        uomId,
        uomName: formik.values.saleType,
      })
        ? convertMainUOM_To_BaseUOM({
            uomId,
            quantity: formik.values.ironRodQuantity,
          })
        : formik.values.ironRodQuantity;
    } else {
      // GIGC: CONVERT TO PCS (BASE UOM)
      quantity =
        lowerCase(formik.values.saleType) === "tons" && selectedItem?.Item_Desc
          ? tonsToPcs(
              formik.values.ironRodQuantity,
              selectedItem?.Item_Desc,
              itemMeasurements
            )
          : formik.values.ironRodQuantity;
    }

    formik.setFieldValue("Quantity", quantity);
  }, [
    formik.values.ironRodQuantity,
    formik.values.saleType,
    selectedItem?.Item_Desc,

    // NEW UOM CODE
    selectedItem?.uomId,
  ]);

  const sizes = useMemo(() => {
    return selectedItem && selectedItem?.sizes
      ? selectedItem?.sizes?.map(el => ({
          ...el,
          label: `${el.Size_Color} - ${el.Quantity}`,
          value: el.Size_Color,
        }))
      : [];
  }, [selectedItem]);

  return (
    <Modal
      show={true}
      onHide={() => setEditedItemIndex(null)}
      dialogClassName="item-select-modal edit"
      backdropClassName={`global-backdrop`}
      centered={true}
      animation={false}
      enforceFocus={false}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h1>Edit Item Details</h1>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="body-content">
          <div className="postion-relative">
            <FormikProvider value={formik}>
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Form.Group className="mb-3 row">
                  <Form.Group className="mb-2 col-4">
                    <Form.Label className="slim">Item Name</Form.Label>
                    <p className="selected">
                      {selectedItem?.Item_Name || "..."}{" "}
                    </p>
                  </Form.Group>

                  <div className="col text-nowrap">
                    <Form.Label className="slim">Item Code</Form.Label>
                    <p className="selected">
                      {" "}
                      {selectedItem?.Bar_Code || "..."}
                    </p>
                  </div>

                  <div className="col-4">
                    <Form.Label className="slim">Size/Desc</Form.Label>
                    <p className="selected">
                      {truncate(
                        selectedItem?.Item_Desc || selectedItem?.Product_Model
                      )}
                    </p>
                  </div>

                  {selectedItem?.quantityInStock ? (
                    <div className="col">
                      <Form.Label className="slim">
                        Quantity in Stock
                      </Form.Label>
                      <p className="selected">
                        {selectedItem?.quantityInStock
                          ? qtyFormatToString(
                              qtyFormat(
                                selectedItem?.quantityInStock,
                                selectedItem.Item_Desc,
                                itemMeasurements
                              )
                            )
                          : "..."}
                      </p>
                    </div>
                  ) : null}

                  <div className="col-4">
                    {isAdmin || !generalSettings.hideUnitCost ? (
                      <>
                        <Form.Label className="slim">Cost Price</Form.Label>
                        <p className="selected">
                          {currency(selectedItem?.UnitCost, {
                            symbol: "",
                          }).format() || "..."}
                        </p>{" "}
                      </>
                    ) : null}
                  </div>
                </Form.Group>
                <hr />
                {/*  */}

                {/* <Form.Group className="mb-3 row">
                  <Form.Group className="mb-2 col-4">
                    <Form.Label className="slim">
                      Supply Center Quantity
                    </Form.Label>
                    <p className="selected">
                      {selectedItem?.salesAnalysis
                        ? qtyFormatToString(
                            qtyFormat(
                              selectedItem?.salesAnalysis,
                              selectedItem.Item_Desc,
                              itemMeasurements
                            )
                          )
                        : "..."}
                    </p>
                  </Form.Group>

                  <div className="col text-nowrap">
                    <Form.Label className="slim">
                      Pre-order <br /> Quantity
                    </Form.Label>
                    <p className="selected">
                      {selectedItem?.preOrder
                        ? qtyFormatToString(
                            qtyFormat(
                              selectedItem?.preOrder,
                              selectedItem.Item_Desc,
                              itemMeasurements
                            )
                          )
                        : "..."}
                    </p>
                  </div>

                  <div className="col-4">
                    <Form.Label className="slim">
                      Approved Proforma Quantity
                    </Form.Label>
                    <p className="selected">
                      {selectedItem?.proforma
                        ? qtyFormatToString(
                            qtyFormat(
                              selectedItem?.proforma,
                              selectedItem.Item_Desc,
                              itemMeasurements
                            )
                          )
                        : "..."}
                    </p>
                  </div>

                  <div className="col">
                    <Form.Label className="slim">Available Quantity</Form.Label>
                    <p className="selected">
                      {selectedItem?.availableQty
                        ? qtyFormatToString(
                            qtyFormat(
                              selectedItem?.availableQty,
                              selectedItem.Item_Desc,
                              itemMeasurements
                            )
                          )
                        : "..."}
                    </p>
                  </div>
                </Form.Group> */}

                {/* <hr /> */}

                {/*  */}
                {usage === "sale" ? (
                  <>
                    <h2 className="pt-0">Pricing Details</h2>
                    <div className="d-flex">
                      {isAdmin || !generalSettings.hideUnitPrice ? (
                        <Form.Group className="mb-3 pb-1">
                          <Form.Label>Unit Price</Form.Label>
                          <CurrencyCustomInput
                            name="UnitPrice"
                            value={formik.values.UnitPrice}
                            isInvalid={
                              formik.touched.UnitPrice &&
                              !!formik.errors.UnitPrice
                            }
                            onValueChange={(value, name) => {
                              // formik.setFieldValue(name, value)
                            }}
                            placeholder="0.00"
                            decimalsLimit={2}
                          />
                          {formik.touched.UnitPrice &&
                          !!formik.errors.UnitPrice ? (
                            <span className="custom-invalid-feedback">
                              {formik.errors.UnitPrice}
                            </span>
                          ) : null}
                        </Form.Group>
                      ) : null}

                      {isAdmin || !generalSettings.hideDiscount ? (
                        <Form.Group className="mb-3 pb-1">
                          <Form.Label>
                            Discount <span className="slim">(optional)</span>
                          </Form.Label>
                          <CurrencyCustomInput
                            name="Discount"
                            value={formik.values.Discount}
                            onValueChange={(value, name) =>
                              handleDiscountChange(value)
                            }
                            placeholder="0.00"
                            decimalsLimit={2}
                          />
                        </Form.Group>
                      ) : null}
                    </div>
                    <Form.Group className="mb-3 pb-1">
                      <Form.Label>Final Selling price</Form.Label>
                      <CurrencyCustomInput
                        name="PriceSold"
                        placeholder="0.00"
                        decimalsLimit={2}
                        value={formik.values.PriceSold}
                        onValueChange={(value, name) =>
                          formik.setFieldValue(name, value)
                        }
                        disabled={
                          !isAdmin && generalSettings?.lockFinalSellingPrice
                        }
                      />
                      {formik.touched.PriceSold && !!formik.errors.PriceSold ? (
                        <span className="custom-invalid-feedback">
                          {formik.errors.PriceSold}
                        </span>
                      ) : null}
                    </Form.Group>
                  </>
                ) : null}

                <Form.Group className="mb-3 pb-2">
                  <Form.Label>
                    <QuantityLabel />{" "}
                  </Form.Label>
                  <div className="duration">
                    <div>
                      <NumberCustomInput
                        placeholder="0"
                        name="ironRodQuantity"
                        value={formik.values.ironRodQuantity}
                        onValueChange={(value, name) => {
                          console.log({ value, name });
                          formik.setFieldValue(name, value, true);
                        }}
                        isInvalid={
                          formik.touched.ironRodQuantity &&
                          !!formik.errors.ironRodQuantity
                        }
                        onBlur={() =>
                          formik.setFieldTouched("ironRodQuantity", true)
                        }
                        allowNegativeValue={false}
                        disabled={!isEmpty(selectedItem?.serialNumberItems)}
                        allowDecimals={true}
                      />
                      {formik.touched.ironRodQuantity &&
                      !!formik.errors.ironRodQuantity ? (
                        <span className="custom-invalid-feedback">
                          {formik.errors.ironRodQuantity}
                        </span>
                      ) : null}
                    </div>

                    <Select
                      classNamePrefix={"form-select"}
                      placeholder={"Select"}
                      isSearchable={false}
                      // value={saleTypes.find(
                      //   el => el.value === formik.values.saleType
                      // )}
                      // options={saleTypes}

                      value={(formik.values.uomId
                        ? getUnitsByUomById({ uomId: formik.values.uomId })
                        : saleTypes
                      ).find(el => el.value === formik.values.saleType)}
                      //
                      options={
                        formik.values.uomId
                          ? getUnitsByUomById({ uomId: formik.values.uomId })
                          : unitsResolver(saleTypes, formik.values.saleType)
                      }
                      onChange={({ value }) => {
                        formik.setFieldValue("saleType", value);
                        handleSaleType(value);
                      }}
                      isDisabled={isEmpty(formik.values.uomId)}
                    />
                  </div>
                </Form.Group>
                <Form.Group className="mb-3 pb-1">
                  <NumberKeypad
                    quantity={selectedItem?.quantityInStock}
                    onKeyPress={key => {
                      let initialValue = String(
                        formik.values.ironRodQuantity
                      ).split(".")[0];
                      if (firstKey) {
                        initialValue = "";
                        setFirstKey(false);
                      }
                      const value = initialValue + key;
                      formik.setFieldValue("ironRodQuantity", value, true);
                      console.log(key);
                    }}
                    onClear={() =>
                      formik.setFieldValue("ironRodQuantity", "0", true)
                    }
                    onDelete={() => {
                      let initialValue = String(
                        formik.values.ironRodQuantity
                      ).split(".")[0];
                      const value = initialValue.slice(0, -1);
                      formik.setFieldValue("ironRodQuantity", value, true);
                    }}
                  />
                </Form.Group>
                {appSettings.hasSize ? (
                  <Form.Group className="mb-3 pb-1">
                    <Form.Label>Size / Color</Form.Label>
                    <Select
                      classNamePrefix={"form-select"}
                      name="Size"
                      options={sizes}
                      value={sizes.find(el => el.value === formik.values.Size)}
                      onChange={({ value }) => {
                        formik.setFieldValue("Size", value);
                      }}
                      isDisabled={true}
                    />
                    {formik.touched.Size && !!formik.errors.Size ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.Size}
                      </span>
                    ) : null}
                  </Form.Group>
                ) : null}

                {usage === "selector" ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Remark</Form.Label>
                      <Form.Control
                        as={"textarea"}
                        name="remark"
                        placeholder="Enter Remark"
                        value={formik.values.remark}
                        onChange={formik.handleChange}
                        rows={2}
                      />
                    </Form.Group>
                  </>
                ) : (
                  <></>
                )}

                {/*  <Form.Check
                type="switch"
                id="custom-switch"
                label="Enable warranty"
                className="mb-3"
                name="Warranty"
                checked={formik.values.Warranty}
                onChange={formik.handleChange}
              /> */}

                {/*  <Form.Group className="mb-3 pb-2">
                <Form.Label>Set Duration</Form.Label>

                <div className="duration">
                  <NumberCustomInput
                    placeholder="0"
                    name="durationNumber"
                    value={formik.values.durationNumber}
                    onValueChange={(value, name) => {
                      formik.setFieldValue(name, value, true);
                    }}
                    // isInvalid={formik.touched.Quantity && !!formik.errors.Quantity}
                  />

                  <Select
                    classNamePrefix={"form-select"}
                    placeholder={"Month"}
                    value={durationOptions.find(
                      (el) => el.value === formik.values.durationValue
                    )}
                    options={durationOptions}
                    onChange={({ value }) =>
                      formik.setFieldValue("durationValue", value)
                    }
                  />
                </div>
              </Form.Group> */}

                {formik.values?.serialNumberItems && (
                  <>
                    <hr />
                    <Form.Group className="mb-3">
                      <Form.Label>Serial Numbers</Form.Label>

                      <FieldArray
                        name="serialNumberItems"
                        render={arrayHelpers => (
                          <>
                            <div className="row">
                              {formik.values.serialNumberItems?.map(
                                (el, index) => (
                                  <div className="col-11">
                                    <div className="d-flex gap-3 mb-2">
                                      <Form.Control
                                        name={`serialNumberItems[${index}].serialNum`}
                                        placeholder="Serial Number"
                                        value={
                                          formik.values.serialNumberItems[index]
                                            .serialNum
                                        }
                                        onChange={formik.handleChange}
                                      />
                                      <Button
                                        title="Remove"
                                        variant=""
                                        type="button"
                                        size="xs"
                                        onClick={() => {
                                          // Remove batch ---------------------------------
                                          const batchIndex = selectedItem.batches.findIndex(
                                            batch =>
                                              batch.Bar_Code ===
                                              formik.values.serialNumberItems[
                                                index
                                              ].BatchCode
                                          );
                                          const modifiedBatches =
                                            batchIndex !== -1
                                              ? [
                                                  ...selectedItem.batches.slice(
                                                    0,
                                                    batchIndex
                                                  ),
                                                  ...selectedItem.batches.slice(
                                                    batchIndex + 1
                                                  ),
                                                ]
                                              : [...selectedItem.batches];

                                          setselectedItem({
                                            ...selectedItem,
                                            batches: modifiedBatches,
                                          });

                                          arrayHelpers.remove(index);
                                          formik.setFieldValue(
                                            "ironRodQuantity",
                                            Number(
                                              formik.values.ironRodQuantity
                                            ) - 1,
                                            true
                                          );
                                        }}
                                      >
                                        ✖
                                      </Button>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>

                            <div>
                              <span className="custom-invalid-feedback">
                                {formik.errors.serialNumberItems}
                              </span>
                            </div>
                          </>
                        )}
                      />
                    </Form.Group>
                  </>
                )}

                <Button
                  disabled={!selectedItem || isLoading}
                  type="submit"
                  className="w-100 submit-btn"
                >
                  Save changes
                </Button>
              </Form>{" "}
            </FormikProvider>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

const NumberKeypad = ({ quantity, onKeyPress, onClear, onDelete }) => {
  // Handle button click
  const handleButtonClick = value => {
    if (value === "Clear") {
      onClear(); // Call the clear callback
    } else if (value === "Delete") {
      onDelete(); // Call the delete callback
    } else if (value <= quantity) {
      onKeyPress(value); // Call the key press callback for numbers
    }
  };

  return (
    <div style={styles.keypad}>
      {[...Array(9).keys()].map(num => {
        let number = num + 1;
        return (
          <button
            type="button"
            key={number}
            style={{
              ...styles.button,
              ...(number > quantity && styles.disabledButton), // Disable buttons greater than quantity
            }}
            onClick={() => handleButtonClick(number)}
            disabled={number > quantity} // Disable buttons greater than quantity
          >
            {number}
          </button>
        );
      })}
      {/* Clear Button */}
      <button
        type="button"
        style={{ ...styles.button, ...styles.specialButton }}
        onClick={() => handleButtonClick("Clear")}
      >
        <svg
          width="16"
          height="16"
          fill="currentColor"
          className="bi bi-x-circle"
          viewBox="0 0 16 16"
        >
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
        </svg>
      </button>
      {/* Number 0 Button */}
      <button
        type="button"
        key={0}
        style={{
          ...styles.button,
        }}
        onClick={() => handleButtonClick(0)}
      >
        0
      </button>
      {/* Delete Button */}
      <button
        type="button"
        style={{ ...styles.button, ...styles.specialButton }}
        onClick={() => handleButtonClick("Delete")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          className="bi bi-backspace"
          viewBox="0 0 16 16"
        >
          <path d="M5.83 5.146a.5.5 0 0 0 0 .708L7.975 8l-2.147 2.146a.5.5 0 0 0 .707.708l2.147-2.147 2.146 2.147a.5.5 0 0 0 .707-.708L9.39 8l2.146-2.146a.5.5 0 0 0-.707-.708L8.683 7.293 6.536 5.146a.5.5 0 0 0-.707 0z" />
          <path d="M13.683 1a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-7.08a2 2 0 0 1-1.519-.698L.241 8.65a1 1 0 0 1 0-1.302L5.084 1.7A2 2 0 0 1 6.603 1zm-7.08 1a1 1 0 0 0-.76.35L1 8l4.844 5.65a1 1 0 0 0 .759.35h7.08a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
        </svg>
      </button>
    </div>
  );
};

// Styles for the keypad
const styles = {
  keypad: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "2px",
    maxWidth: "200px",
    margin: "0 auto",
  },
  button: {
    padding: "15px",
    fontSize: "18px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    backgroundColor: "#f9f9f9",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  disabledButton: {
    backgroundColor: "#e0e0e0",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  specialButton: {
    backgroundColor: "#ff6b6b",
    color: "#fff",
  },
};
